import { useEffect, useRef, useState } from 'react';
import type { Ticket } from '@/lib/api';
import type { TicketState } from '../../../src/types';
import { useToast } from '@/components/Toast';
import { CoreIcon } from '@/components/CoreIcon';
import { cn } from '@/lib/utils';

/**
 * Unified compose dock — mirrors the reference design's bottom compose bar.
 *
 * Behaviour:
 *  - If an AI draft exists (state.currentDraft), it is rendered INLINE above
 *    the textarea as a "Fin draft" card with Approve / Regenerate / Reject.
 *  - The textarea doubles as the manual-reply field; the AI draft is loaded
 *    into it so the operator can lightly edit before approving.
 *  - "AI Assist" regenerates when no draft exists (kicks off runTriage).
 */
export function ComposeBar(props: {
  ticket: Ticket;
  state: TicketState;
  onApprove: (draftId: string, editedBody?: string) => Promise<unknown>;
  onReject: (draftId: string, reason?: string) => Promise<unknown>;
  onRegenerate: () => Promise<unknown>;
  onManualReply: (body: string) => Promise<unknown>;
  onMarkSpam: () => Promise<unknown>;
}) {
  const toast = useToast();
  const draft = props.state.currentDraft;
  const [body, setBody] = useState<string>(draft?.body ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [draftEditing, setDraftEditing] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // When a new AI draft arrives, pre-fill the composer (unless the operator
  // was already typing — don't clobber their work).
  useEffect(() => {
    if (!draft) return;
    if (!body || body === '' || !draftEditing) {
      setBody(draft.body);
      setDraftEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id]);

  const run = async (key: string, fn: () => Promise<unknown>, success?: string) => {
    setBusy(key);
    try {
      await fn();
      if (success) toast.success(success);
    } catch (err) {
      console.error(key, err);
      toast.error('Action failed', (err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const send = () => {
    const text = body.trim();
    if (!text) { toast.info('Write a reply first'); return; }
    if (draft) {
      void run('approve', () => props.onApprove(draft.id, text).then(() => setBody('')), 'Reply sent');
    } else {
      void run('manual', () => props.onManualReply(text).then(() => setBody('')), 'Reply sent');
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl-Enter to send — matches most chat apps.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="border-t border-border bg-surface">
      {/* AI draft ribbon — shows only when the agent has prepared a reply. */}
      {draft && (
        <div className="px-5 pt-3">
          <div
            className="rounded-lg border p-3 relative"
            style={{
              background: 'linear-gradient(180deg, #fff6ef 0%, #fffaf5 100%)',
              borderColor: '#ffd3b3',
            }}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 text-[12px] font-medium" style={{ color: '#7a2a00' }}>
                <span className="text-accent"><CoreIcon name="sparkles" size={14} /></span>
                Fin draft ready
                <span className="mono-label !text-[10px]" style={{ color: '#7a2a00' }}>
                  · {draft.model}
                  · {Math.round(draft.confidence * 100)}% confidence
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="btn-ghost !h-7 !px-2 text-xs"
                  disabled={busy !== null}
                  onClick={() => run('regen', () => props.onRegenerate())}
                  aria-label="Regenerate AI draft"
                >
                  <span className={cn(busy === 'regen' && 'animate-spin inline-block')}>
                    <CoreIcon name="reload" size={12} />
                  </span>
                  {busy === 'regen' ? 'Regenerating…' : 'Regenerate'}
                </button>
                <button
                  type="button"
                  className="btn-ghost !h-7 !px-2 text-xs"
                  disabled={busy !== null}
                  onClick={() => run('reject', () => props.onReject(draft.id, '').then(() => setBody('')))}
                  aria-label="Reject AI draft"
                >
                  <CoreIcon name="x" size={12} />
                  Dismiss
                </button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Review below, edit inline, then send. We never send without your approval.
            </p>
          </div>
        </div>
      )}

      {/* Channel + attach row. */}
      <div className="px-5 pt-3 flex items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2 py-1 font-medium">
          <span className="text-muted-foreground"><CoreIcon name="envelope-closed" size={12} /></span>
          Email
          <span className="text-muted-foreground font-normal">
            · to {props.ticket.from_email}
          </span>
        </span>
        <button
          type="button"
          className="btn-ghost !h-7 !px-2 text-xs ml-auto"
          title="Attach file (coming soon)"
          aria-label="Attach file"
        >
          <CoreIcon name="paperclip" size={14} />
        </button>
      </div>

      {/* Composer. */}
      <div className="px-5 pb-4 pt-2">
        <div className="rounded-lg border border-border bg-background focus-within:border-foreground transition-colors">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => { setBody(e.target.value); if (draft) setDraftEditing(true); }}
            onKeyDown={onKeyDown}
            placeholder={draft ? 'Edit the AI draft before sending…' : 'Write a reply…'}
            aria-label="Reply message"
            rows={4}
            className="w-full bg-transparent resize-none p-3 text-[14px] leading-[1.5] focus:outline-none"
          />
          <div className="flex items-center justify-between px-3 pb-2.5">
            <div className="flex items-center gap-1">
              {!draft && (
                <button
                  type="button"
                  className="btn-ghost !h-7 !px-2 text-xs text-accent"
                  disabled={busy !== null}
                  onClick={() => run('regen', () => props.onRegenerate())}
                  aria-label="Ask AI to draft a reply"
                >
                  <span className={cn(busy === 'regen' && 'animate-pulse inline-block')}>
                    <CoreIcon name="sparkles" size={14} />
                  </span>
                  {busy === 'regen' ? 'Drafting…' : 'AI Assist'}
                </button>
              )}
              <button
                type="button"
                className="btn-ghost !h-7 !px-2 text-xs text-destructive"
                disabled={busy !== null}
                onClick={() => run('spam', () => props.onMarkSpam(), 'Marked as spam')}
                aria-label="Mark as spam"
              >
                <CoreIcon name="warning" size={14} />
                Spam
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="mono-label !text-[10px]">
                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} ↵ to send
              </span>
              <button
                type="button"
                className={cn(draft ? 'btn-accent' : 'btn-primary', '!h-8')}
                disabled={busy !== null || !body.trim()}
                onClick={send}
                aria-label={draft ? 'Send AI reply' : 'Send reply'}
              >
                <CoreIcon name="send" size={14} />
                {busy === 'approve' || busy === 'manual' ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
